import { useQueries, UseQueryResult } from '@tanstack/react-query';
import {
  IntegrationApi,
  IntegrationApiFeaturesGeometryRingsContractsResponsesGetGeometryRingsByPostalCodeResponse as RingsDataProps,
} from '@alliander-fe/api';

type GeometryRingsByPostalCode = {
  [key: string]: number[][][];
};

export type FlattenedRingsDataProps = {
  geometryRingsByPostalCode: GeometryRingsByPostalCode;
};

export type RingsProps = {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  data: FlattenedRingsDataProps | undefined;
};

const CHUNK_SIZE = 2000;

/**
 * This hook is used to get the rings for the congestion report.
 * It request each postal code separately and then combines the data.
 * This is done to increase the performance of the request.
 *
 * @param postalCodes - The postal codes of the affected areas.
 * @returns The rings for the congestion report.
 */
export const useMapRings = ({ postalCodes }: { postalCodes?: string }): RingsProps => {
  const postalCodesArray = postalCodes?.split(', ');

  const postalCodeChunks = postalCodesArray
    ? getPostalCodeChunks(postalCodesArray, CHUNK_SIZE)
    : [];
  const requestPostalCodeChunks = useQueries({
    queries:
      postalCodeChunks?.map((postalCode) => ({
        queryKey: ['ringsQuery', postalCode],
        enabled: Boolean(postalCode.length),
        queryFn: () =>
          IntegrationApi.geometryRingsEndpointsGetGeometryRingsByPostalCode({
            postalCodes: postalCode?.split(', '),
          }),
      })) ?? [],
  });

  const geometryRings = createGeometryRingsObject(requestPostalCodeChunks);
  const isLoading = requestPostalCodeChunks.some((result) => result.isLoading);
  const isSuccess = requestPostalCodeChunks.every((result) => result.isSuccess);
  const isError = requestPostalCodeChunks.every((result) => result.isError);

  return {
    isLoading,
    isSuccess,
    isError,
    data: {
      geometryRingsByPostalCode: geometryRings,
    },
  };
};

/**
 * This function splits the array into chunks of the given size.
 * @param array - The array to split.
 * @param chunkSize - The size of the chunks.
 * @returns The array of chunks.
 */
const getPostalCodeChunks = (array: string[], chunkSize: number) => {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    result.push(chunk.join(', '));
  }
  return result;
};

/**
 * This function flattens the rings array.
 * @param rings - The rings object.
 * @returns The flattened rings array.
 *
 * We need to flatten the postalCode object because the array is nested one layer deeper than we need.
 */
const flattenRingArray = (rings: Record<string, number[][][][]>): GeometryRingsByPostalCode[] => {
  const ringsArray = Object.keys(rings).map((postalCode) => {
    return {
      [postalCode]: [...rings[postalCode].map((ring) => ring[0])],
    };
  });

  return ringsArray;
};

/**
 * This function creates an object with the postal codes as keys and the rings as values.
 * @param responseArray - The array of responses from the API.
 * @returns The object with the postal codes as keys and the rings as values.
 */
const createGeometryRingsObject = (
  responseArray: UseQueryResult<RingsDataProps, Error>[]
): GeometryRingsByPostalCode => {
  const combineResults = responseArray
    .map((response) => {
      if (response.data?.geometryRingsByPostalCode) {
        return flattenRingArray(response.data.geometryRingsByPostalCode);
      }

      return [];
    })
    .flat();

  return Object.assign({}, ...combineResults);
};
